// The ACTIONS view as a DATABASE (owner call 2026-09-10, "I want a database
// table instead of … where you have to click on the item to open up a very bad
// looking sidebar"). One row per action, one column per property the sheet
// carries — status, assignees, due, scheduled, priority, effort, tags — every
// cell edited IN PLACE with the kit's own pieces, the title still the door to
// the sheet. The table is the kit's <DataTable>; nothing here draws a seam.
//
// Writes go the same roads the sheet takes (Sheet.jsx): value_json patches to
// PUT /api/items/:id followed by window.afterNodeWrite (which re-announces
// the mission so this table re-renders from the fresh tree); the schedule to
// PUT /data/schedule/:id like the sheet's saveSched.
import React, { useMemo, useRef, useState } from 'react';
import { DataTable, Menu, StatusBadge, Avatar, Chip } from '@ofself/shell';
import Outliner, { collectActionTree } from './Outliner';

const STATUSES = [
  { key: 'open', label: 'open', tone: 'neutral' },
  { key: 'in_progress', label: 'in progress', tone: 'accent' },
  { key: 'reviewing', label: 'reviewing', tone: 'info' },
  { key: 'paused', label: 'paused', tone: 'warning' },
  { key: 'completed', label: 'done', tone: 'success' },
  { key: 'not_needed', label: 'not needed', tone: 'muted' },
];
const LEVELS = [
  { key: 'low', label: 'low' },
  { key: 'medium', label: 'medium' },
  { key: 'high', label: 'high' },
];
const canon = (s) => window.canonStatus?.(s) ?? s;
const DONE = new Set(['completed', 'not_needed']);

// the tree flattened in reading order — goals are transparent (Outliner's law)
function flatten(tree, depth = 0, out = []) {
  for (const { node, children } of tree) {
    out.push({ node, depth });
    if (children?.length) flatten(children, depth + 1, out);
  }
  return out;
}

async function patchNode(id, vj) {
  try {
    await window.putJson(`/api/items/${id}`, { value_json: vj });
    await window.afterNodeWrite?.(id, vj);
  } catch (err) { alert(`Update failed: ${err.message}`); }
}

// ── cells ──────────────────────────────────────────────────────────────────
function StatusCell({ node }) {
  const cur = STATUSES.find((s) => s.key === canon(node.status)) || STATUSES[0];
  return (
    <Menu
      align="start"
      trigger={
        <button type="button" className="ofself-dt__cellbtn" aria-label="Status">
          <StatusBadge tone={cur.tone}>{cur.label}</StatusBadge>
        </button>
      }
      items={STATUSES.map((s) => ({
        label: s.label,
        onSelect: () => s.key !== cur.key && patchNode(node.id, { status: s.key }),
      }))}
    />
  );
}

function LevelCell({ node, field }) {
  const v = node[field];
  const cur = LEVELS.find((l) => l.key === v);
  return (
    <Menu
      align="start"
      trigger={
        <button type="button" className={`ofself-dt__cellbtn${cur ? '' : ' ofself-dt__cellbtn--empty'}`} aria-label={field}>
          {cur ? cur.label : 'empty'}
        </button>
      }
      items={[
        ...LEVELS.map((l) => ({ label: `${l.key === v ? '✓ ' : ''}${l.label}`, onSelect: () => l.key !== v && patchNode(node.id, { [field]: l.key }) })),
        ...(cur ? [{ separator: true }, { label: 'clear', onSelect: () => patchNode(node.id, { [field]: null }) }] : []),
      ]}
    />
  );
}

function AssigneesCell({ node, members }) {
  const ids = Array.isArray(node.assignee_ids) ? node.assignee_ids : [];
  const names = ids.map((id) => window.resolveMemberName?.(id) || id);
  const toggle = (id) => {
    const next = ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
    patchNode(node.id, { assignee_ids: next });
  };
  return (
    <Menu
      align="start"
      trigger={
        <button type="button" className={`ofself-dt__cellbtn${ids.length ? '' : ' ofself-dt__cellbtn--empty'}`} aria-label="Assignees">
          {ids.length
            ? <span className="ofself-dt__run">{names.map((n, i) => <Avatar key={ids[i]} name={n} size="sm" />)}<span>{names.length === 1 ? names[0] : `${names.length} people`}</span></span>
            : 'unassigned'}
        </button>
      }
      items={members.length
        ? members.map((m) => ({ label: `${ids.includes(m.user_id) ? '✓ ' : ''}${m.username}`, onSelect: () => toggle(m.user_id) }))
        : [{ label: 'no teammates in this scope', disabled: true }]}
    />
  );
}

function TagsCell({ node }) {
  const worn = window.tagsForNode?.(node) || node.tags || [];
  const all = window.allTags?.() || [];
  const wornIds = new Set(worn.map((t) => t.id));
  const save = (tags) => patchNode(node.id, { tags: tags.map((t) => ({ id: t.id, label: t.label })) });
  const toggle = (t) => save(wornIds.has(t.id) ? worn.filter((w) => w.id !== t.id) : [...worn, t]);
  return (
    <span className="ofself-dt__run">
      {worn.map((t) => <Chip key={t.id} onRemove={() => toggle(t)}>{t.label}</Chip>)}
      <Menu
        align="start"
        trigger={<button type="button" className="ofself-dt__cellbtn ofself-dt__cellbtn--empty" aria-label="Add tag">{worn.length ? '+' : '+ tag'}</button>}
        items={all.length
          ? all.map((t) => ({ label: `${wornIds.has(t.id) ? '✓ ' : ''}${t.label}`, onSelect: () => toggle(t) }))
          : [{ label: 'no tags yet — make one in the sheet', disabled: true }]}
      />
    </span>
  );
}

function DueCell({ node }) {
  const d = useRef(null); const t = useRef(null);
  const save = async () => {
    const ymd = d.current.value;
    if (ymd && !t.current.value) t.current.value = '23:59';
    const due_at = ymd ? window.withLocalOffset(`${ymd}T${t.current.value || '23:59'}:00`) : null;
    try {
      await window.putJson(`/api/items/${node.id}`, { value_json: { due_at } });
      await window.afterNodeWrite?.(node.id, { due_at, target_date: due_at ? due_at.slice(0, 10) : null });
    } catch (err) { alert(`Update failed: ${err.message}`); }
  };
  const ymd = (node.due_at || '').slice(0, 10) || node.target_date || '';
  const hm = node.has_due_time === false ? '' : ((node.due_at || '').slice(11, 16) || '');
  return (
    <span className="ofself-dt__run">
      <input ref={d} type="date" className="ofself-dt__input" defaultValue={ymd} onChange={save} aria-label="Due date" />
      {ymd && <input ref={t} type="time" className="ofself-dt__input" defaultValue={hm} onChange={save} aria-label="Due time" />}
      {!ymd && <input ref={t} type="hidden" defaultValue="" />}
    </span>
  );
}

function localYmd(dt) {
  const p = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}`;
}
function ScheduledCell({ node }) {
  const d = useRef(null); const s = useRef(null); const e = useRef(null);
  const save = async () => {
    const ymd = d.current.value;
    if (!ymd) {
      try {
        await window.putJson(`/data/schedule/${node.id}`, { scheduled_at: null, scheduled_end_at: null });
        await window.afterNodeWrite?.(node.id, { scheduled_at: null, scheduled_end_at: null });
        window.loadSchedule?.();
      } catch (err) { alert(`Update failed: ${err.message}`); }
      return;
    }
    let st = s.current?.value;
    if (!st) {
      const now = new Date();
      if (ymd === localYmd(now)) {
        const mins = now.getHours() * 60 + now.getMinutes();
        const next = Math.ceil((mins + 1) / 30) * 30;
        st = next >= 24 * 60 ? '23:30' : `${String(Math.floor(next / 60)).padStart(2, '0')}:${String(next % 60).padStart(2, '0')}`;
      } else st = '09:00';
    }
    let en = e.current?.value;
    if (!en) {
      const m = parseInt(st.slice(0, 2), 10) * 60 + parseInt(st.slice(3, 5), 10) + 45;
      en = `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
    }
    try {
      const sAt = window.withLocalOffset(`${ymd}T${st}:00`);
      const eAt = window.withLocalOffset(`${ymd}T${en}:00`);
      await window.putJson(`/data/schedule/${node.id}`, { scheduled_at: sAt, scheduled_end_at: eAt });
      await window.afterNodeWrite?.(node.id, { scheduled_at: sAt, scheduled_end_at: eAt });
      window.loadSchedule?.();
    } catch (err) { alert(`Update failed: ${err.message}`); }
  };
  const ymd = (node.scheduled_at || '').slice(0, 10);
  const st = (node.scheduled_at || '').slice(11, 16);
  const en = (node.scheduled_end_at || '').slice(11, 16);
  return (
    <span className="ofself-dt__run">
      <input ref={d} type="date" className="ofself-dt__input" defaultValue={ymd} onChange={save} aria-label="Scheduled date" />
      {ymd && <input ref={s} type="time" className="ofself-dt__input" defaultValue={st} onChange={save} aria-label="Start" />}
      {ymd && <span aria-hidden="true">–</span>}
      {ymd && <input ref={e} type="time" className="ofself-dt__input" defaultValue={en} onChange={save} aria-label="End" />}
    </span>
  );
}

// the add row: the same seam the outliner's add bar takes (window.olCommitAdd)
function AddRow({ parentId, open, setOpen }) {
  const [title, setTitle] = useState('');
  const commit = async () => {
    const t = title.trim();
    if (!t) { setOpen(false); return; }
    setTitle('');
    await window.olCommitAdd?.(parentId, 'action', t);
  };
  if (!open) {
    return (
      <button type="button" className="ofself-dt__cellbtn ofself-dt__cellbtn--empty" onClick={() => setOpen(true)}>+ new action</button>
    );
  }
  return (
    <input
      autoFocus
      className="ofself-dt__input"
      style={{ fontFamily: 'inherit', width: '100%' }}
      placeholder="what needs to be done"
      value={title}
      onChange={(e) => setTitle(e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setTitle(''); setOpen(false); } }}
      onBlur={commit}
    />
  );
}

// ── the table ──────────────────────────────────────────────────────────────
const LEVEL_RANK = { high: 0, medium: 1, low: 2 };
const STATUS_RANK = Object.fromEntries(STATUSES.map((s, i) => [s.key, i]));
// List leads: the kit opens on views[0] when nothing is saved (owner call
// 2026-09-24, the list is the default reading and the first tab; a view
// someone has already chosen is still theirs).
const VIEWS = [
  { id: 'list', label: 'List', glyph: '≡' },
  { id: 'table', label: 'Table', glyph: '▤' },
  { id: 'board', label: 'Board', glyph: '▥' },
];

// `views` — what the non-table views render, keyed by id (the board comes
// from Mission.jsx, which owns its columns); `onAddRequest` is unused by the
// table itself: the bar's "+ new" opens the footer's add row.
export default function ActionsTable({ mission, views = {} }) {
  const rows = useMemo(() => flatten(collectActionTree(mission.items || [])), [mission]);
  const members = window.activeGroupMembers?.() || [];
  const grouped = members.length > 0;
  const [adding, setAdding] = useState(false);

  const columns = useMemo(() => [
    { id: 'title', label: 'action', glyph: 'Aa', width: 'minmax(280px, 2fr)', primary: true,
      sortValue: (r) => r.node.title, searchText: (r) => r.node.title },
    { id: 'status', label: 'status', glyph: '⌄', width: '150px',
      sortValue: (r) => STATUS_RANK[canon(r.node.status)] ?? 99,
      filterValues: (r) => [canon(r.node.status)],
      filterOptions: STATUSES.map((st) => ({ value: st.key, label: st.label })) },
    ...(grouped ? [{ id: 'assignees', label: 'assignees', glyph: '◎', width: '180px',
      sortValue: (r) => (r.node.assignee_ids?.[0] ? window.resolveMemberName?.(r.node.assignee_ids[0]) : null),
      filterValues: (r) => (r.node.assignee_ids?.length ? r.node.assignee_ids : ['unassigned']),
      filterOptions: [{ value: 'unassigned', label: 'unassigned' }, ...members.map((m) => ({ value: m.user_id, label: m.username }))] }] : []),
    { id: 'due', label: 'deadline', glyph: '☷', width: '230px', hint: 'the moment it must be done by',
      sortValue: (r) => r.node.due_at || r.node.target_date || null },
    { id: 'scheduled', label: 'on calendar', glyph: '☷', width: '290px', hint: 'the block of time set aside to do it',
      sortValue: (r) => r.node.scheduled_at || null },
    { id: 'priority', label: 'priority', glyph: '⌄', width: '120px',
      sortValue: (r) => (r.node.priority in LEVEL_RANK ? LEVEL_RANK[r.node.priority] : null),
      filterValues: (r) => [r.node.priority || 'empty'],
      filterOptions: [...LEVELS.map((l) => ({ value: l.key, label: l.label })), { value: 'empty', label: 'empty' }] },
    { id: 'effort', label: 'effort', glyph: '⌄', width: '120px',
      sortValue: (r) => (r.node.effort in LEVEL_RANK ? LEVEL_RANK[r.node.effort] : null),
      filterValues: (r) => [r.node.effort || 'empty'],
      filterOptions: [...LEVELS.map((l) => ({ value: l.key, label: l.label })), { value: 'empty', label: 'empty' }] },
    { id: 'tags', label: 'tags', glyph: '≣', width: 'minmax(200px, 1fr)',
      sortValue: (r) => (window.tagsForNode?.(r.node) || r.node.tags || []).map((t) => t.label).sort().join(', ') || null,
      searchText: (r) => (window.tagsForNode?.(r.node) || r.node.tags || []).map((t) => t.label).join(' '),
      filterValues: (r) => (window.tagsForNode?.(r.node) || r.node.tags || []).map((t) => t.label) },
  ], [grouped, members]);

  const renderCell = (col, { node, depth }) => {
    switch (col.id) {
      case 'title':
        return (
          <>
            {/* the kind glyph retired (owner call 2026-09-10, "redundant"): a
                habit still tells itself apart by its cadence word */}
            {node.kind === 'habit' && <span className="ofself-dt__kind" aria-hidden="true" style={{ opacity: .45 }}>↻</span>}
            <span>{node.title}</span>
          </>
        );
      case 'status': return <StatusCell node={node} />;
      case 'assignees': return <AssigneesCell node={node} members={members} />;
      case 'due': return <DueCell node={node} />;
      case 'scheduled': return <ScheduledCell node={node} />;
      case 'priority': return <LevelCell node={node} field="priority" />;
      case 'effort': return <LevelCell node={node} field="effort" />;
      case 'tags': return <TagsCell node={node} />;
      default: return null;
    }
  };

  return (
    <DataTable
      className="missions-actions-table"
      storageKey="missions-actions-table"
      columns={columns}
      rows={rows}
      rowKey={(r) => r.node.id}
      renderCell={renderCell}
      onRowOpen={(r) => window.openSheet?.(r.node.id)}
      rowProps={(r) => ({
        'data-done': DONE.has(canon(r.node.status)) || undefined,
        /* the family step: the kit draws the indent from this stamp */
        'data-depth': r.depth,
        style: { '--depth': r.depth },
      })}
      views={VIEWS}
      renderView={(id) => {
        if (id === 'board') return views.board ?? null;
        /* THE TABLE (owner call 2026-09-20, back after 90adc22 retired it):
           null hands the kit its own grid — the database reading. */
        if (id === 'table') return null;
        /* THE LIST (owner call 2026-09-10, "just in case there's any loss"):
           the outliner as it stood — tree lines, drag to reorder, nested adds.
           It is also the FALLBACK: the kit draws its own grid whenever this
           returns null, so a stale storageKey view (the calendar / by-person /
           timeline ids retired in 90adc22) would otherwise bring a removed
           view back as the grid. Anything unrecognised lands on the list. */
        return <div className="outliner missions-actions-list"><Outliner mission={mission} /></div>;
      }}
      onAdd={() => setAdding(true)}
      addLabel="new action"
      searchPlaceholder="search actions"
      footer={<AddRow parentId={mission.id} open={adding} setOpen={setAdding} />}
      emptyLabel="no actions yet"
    />
  );
}
